import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material/styles';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CalendarViewSensorArray from './CalendarViewSensorArray';
import {
  getActiveMarker,
  getCurrentLocation,
  getHighlightedItem,
  getLocationsById,
  getSelectedEndDate,
  getSelectedHours,
  getSelectedSensorIds,
  getSelectedStartDate,
  getSensorsById,
} from '../../state/selectors';
import { VarName } from '../../services/api';
import {
  ValueType,
  HeatmapHoverSelection,
  CalendarSelectionType,
  SensorFilteredStats,
  SensorAllStats,
  PlotType,
} from './helpers';
import { ActiveItem } from '../../state/types';
import { setSelectedSensors } from '../../state/actions';
import CalendarViewPlot from '../Plots/HeatMap/CalendarViewPlot';
import WeekViewPlot from '../Plots/HeatMap/WeekViewPlot';
import { varNameDetails } from '../../utils/varNames';
import LoaderWithBackdrop from '../HelperComponents/LoaderWithBackdrop';
import { combineStats, Stats } from '../../utils/statistics';
import ActiveMarkerSelection from '../HelperComponents/ActiveMarkerSelection';
import { useAppDispatch, useAppSelector } from '../../state/store';
import TagViewSummary from './TagViewSummary';
import useSensorDetailsInLoc from '../LocationConfig/Tagging/useSensorDetailsInLoc';
import { getAllSpaces } from '../Map/mapHelpers';
import useStyles from '../../styles';

dayjs.extend(weekOfYear);
dayjs.extend(localizedFormat);

interface CalendarViewDataContainerProps {
  sensorStats: Map<string, SensorAllStats>;
  sensorFilteredStats: Map<string, SensorFilteredStats>;
  heatmapHoverSelection: HeatmapHoverSelection | undefined;
  setHeatmapHoverSelection: (value: HeatmapHoverSelection | undefined) => void;
  selectionType: CalendarSelectionType;
  downloadData: boolean;
  setDownloadData: (value: boolean) => void;
  loading: boolean;
}

enum DataType {
  Sensor = 'Sensor',
  Tags = 'Tags',
}

export interface TagSummaryRef {
  clearState: () => void;
}

function CalendarViewDataContainer({
  sensorStats,
  sensorFilteredStats,
  heatmapHoverSelection,
  setHeatmapHoverSelection,
  selectionType,
  downloadData,
  setDownloadData,
  loading,
}: CalendarViewDataContainerProps): JSX.Element {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const classes = useStyles();
  const highlightedItem = useAppSelector(getHighlightedItem);
  const activeMarker = useAppSelector(getActiveMarker);
  const currentLocation = useAppSelector(getCurrentLocation);
  const selectedSensors = useAppSelector(getSelectedSensorIds);
  const [hoveredSensor, setHoveredSensor] = useState<ActiveItem>();
  const startDate = useAppSelector(getSelectedStartDate);
  const endDate = useAppSelector(getSelectedEndDate);
  const sensorsById = useAppSelector(getSensorsById);
  const locationsById = useAppSelector(getLocationsById);
  const hoursSelection = useAppSelector(getSelectedHours);
  const [dataType, setDataType] = useState<DataType>(DataType.Sensor);
  const tagSummaryRef = useRef<TagSummaryRef>(null);
  const sensorsDetailsInLoc = useSensorDetailsInLoc();

  const sensorIdsWithData = useMemo(() => Array.from(sensorStats.keys()), [sensorStats]);

  const allSpaces = useMemo(() => getAllSpaces(sensorsDetailsInLoc) ?? [], [sensorsDetailsInLoc]);

  // display tags summary if available in initial landing
  useEffect(() => {
    if (allSpaces.length > 0) setDataType(DataType.Tags);
    else setDataType(DataType.Sensor);
  }, [allSpaces]);

  useEffect(() => {
    setHoveredSensor(undefined);
    setHeatmapHoverSelection(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarker, currentLocation]);

  // selectedSensors that we actually have data for
  const addedSensors = useMemo(() => {
    const activeSensors = selectedSensors.filter((selectedId) =>
      sensorIdsWithData.some((id) => id === selectedId)
    );
    return activeSensors;
  }, [sensorIdsWithData, selectedSensors]);

  useEffect(() => {
    if (highlightedItem.id && highlightedItem.varName)
      setHoveredSensor({ id: highlightedItem.id, varName: highlightedItem.varName });
    else setHoveredSensor(undefined);
  }, [highlightedItem]);

  const heatmapSensorIds = useMemo(() => {
    let sensors: string[] = [];
    if (addedSensors.length > 0) sensors = addedSensors;
    else if (hoveredSensor) sensors = [hoveredSensor.id];
    return sensors;
  }, [hoveredSensor, addedSensors]);

  const highlightedItemStats = useMemo(
    () => sensorStats.get(highlightedItem.id ?? 'NONE')?.stats ?? undefined,
    [highlightedItem.id, sensorStats]
  );

  const selectedSensorStats = useMemo(() => {
    const statsArray: Stats[] = [];
    let output: Stats = {
      bandTimes: new Map<string, number>(),
      totalTime: 0,
      maxMinAvg: { max: NaN, min: NaN, mean: NaN },
    };
    if (selectedSensors.length > 0) {
      selectedSensors.forEach((id) => {
        const s = sensorStats.get(id)?.stats;
        if (s !== undefined) statsArray.push(s);
      });
      output = combineStats(statsArray);
    }
    return output;
  }, [selectedSensors, sensorStats]);

  const totalSensorStats = useMemo(() => {
    let output: Stats = {
      bandTimes: new Map<string, number>(),
      totalTime: 0,
      maxMinAvg: { max: NaN, min: NaN, mean: NaN },
    };
    if (sensorStats.size > 0) {
      const statsArray = Array.from(sensorStats.values(), (value) => value.stats);
      output = combineStats(statsArray);
    }
    return output;
  }, [sensorStats]);

  const combinedSensorStats = useMemo(() => {
    let combinedStats: Stats = totalSensorStats;
    if (addedSensors.length > 0) combinedStats = selectedSensorStats;
    else if (highlightedItemStats !== undefined) combinedStats = highlightedItemStats;
    return combinedStats;
  }, [addedSensors.length, highlightedItemStats, selectedSensorStats, totalSensorStats]);

  const sensorSelectionLabel = useMemo(() => {
    let label = `${locationsById.get(currentLocation)?.name ?? ''} (${sensorStats.size} ${
      varNameDetails[activeMarker].label
    } sensors)`;
    if (heatmapSensorIds.length > 0) {
      if (heatmapSensorIds.length === 1)
        label = `${sensorsById.get(heatmapSensorIds[0])?.name ?? heatmapSensorIds[0]} (${
          varNameDetails[activeMarker].label
        })`;
      else
        label = `${heatmapSensorIds.length} selected ${varNameDetails[activeMarker].label} sensors`;
    }
    return label;
  }, [
    locationsById,
    currentLocation,
    heatmapSensorIds,
    sensorsById,
    activeMarker,
    sensorStats.size,
  ]);

  // XXX TODO Do we need the utl type? Could just be static?
  const valueType = activeMarker === VarName.MotionEvent ? ValueType.utl : ValueType.avg;

  const hasTotValue = Array.from(sensorStats.values()).some(
    (s) => s.stats.totalAdded !== undefined
  );

  const valuesLabel = useMemo(() => {
    const varNameLabel = varNameDetails[activeMarker].label;
    let label = `${varNameLabel} ${hasTotValue ? 'totals' : 'averages'} `;

    const selectedHoursStr = `${hoursSelection.startHour
      .toFixed()
      .padStart(2, '0')}:00-${hoursSelection.endHour.toFixed().padStart(2, '0')}:00`;
    const selectedHoursOnlyLabel = hoursSelection.selectHours ? ` (${selectedHoursStr})` : '';
    const selectedDaysStr =
      !hoursSelection.selectHours || hoursSelection.includeWeekends ? '' : ' Mon-Fri';
    const selectedDaysOnlyLabel =
      hoursSelection.selectHours && !hoursSelection.includeWeekends
        ? ` (${selectedDaysStr.trim()})`
        : '';
    const hoursSelectionLabel = hoursSelection.selectHours
      ? ` (${selectedHoursStr}${selectedDaysStr})`
      : '';

    if (heatmapHoverSelection) {
      const { date, weekWithStart, dayOfWeek, hour, summaryType } = heatmapHoverSelection;
      const hasHourValue = hour !== undefined;
      const hourLabel = hasHourValue
        ? `${hour.toFixed().padStart(2, '0')}:00-${(hour + 1).toFixed().padStart(2, '0')}:00`
        : '';
      if (summaryType) {
        if (summaryType === PlotType.min) label = `${varNameLabel} minimum values `;
        else if (summaryType === PlotType.max) label = `${varNameLabel} maximum values `;
        else if (summaryType === PlotType.avg) label = `${varNameLabel} averages `;
        else label = `${varNameLabel} ${summaryType}s `;
      }
      if (date) {
        return label
          .concat('for ')
          .concat(dayjs(date).format('MMM DD YYYY'))
          .concat(selectedHoursOnlyLabel); // includeWeekends not relevant
      }
      if (weekWithStart) {
        return label
          .concat('for week starting ')
          .concat(dayjs(date).format('MMM DD YYYY'))
          .concat(hoursSelectionLabel);
      }
      if (dayOfWeek !== undefined) {
        label = label
          .concat('for all ')
          .concat(dayjs(new Date()).day(dayOfWeek).format('dddd'))
          .concat('s ')
          .concat(hourLabel);
        if (!hasHourValue) label = label.concat(selectedHoursOnlyLabel); // includeWeekends not relevant
        return label;
      }
      if (hour !== undefined) {
        return label.concat(`${hourLabel} ${selectedDaysOnlyLabel}`);
      }
    }
    label = label.concat(
      `${dayjs(startDate).format('MMM DD')} - ${dayjs(endDate).format(
        'MMM DD YYYY'
      )}${hoursSelectionLabel}`
    );

    return label;
  }, [hasTotValue, heatmapHoverSelection, startDate, endDate, hoursSelection, activeMarker]);

  const getValuesLabelWithValue = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const plotLabel = parseFloat(heatmapHoverSelection?.hoverValue).toFixed(1);
    const metric = varNameDetails[activeMarker].metric ?? '';
    const label = heatmapHoverSelection ? `${valuesLabel} [${plotLabel}${metric}]` : valuesLabel;
    return label;
  };

  const valuesRange: [number, number] = useMemo(
    () =>
      totalSensorStats?.totalAdded !== undefined && totalSensorStats.values
        ? [0, Math.max(...totalSensorStats.values.totVals)]
        : [totalSensorStats.maxMinAvg.min, totalSensorStats.maxMinAvg.max],
    [totalSensorStats]
  );

  const summaryType = useMemo(() => {
    let summary = PlotType.avg;
    if (heatmapHoverSelection?.summaryType) summary = heatmapHoverSelection.summaryType;
    else if (hasTotValue) summary = PlotType.tot;
    return summary;
  }, [hasTotValue, heatmapHoverSelection?.summaryType]);

  return (
    <>
      <Box
        sx={{
          background: theme.palette.primary.main,
          marginTop: '10px',
          borderRadius: '8px',
          paddingBottom: '10px',
        }}
      >
        <Box sx={{ padding: '10px 10px 20px 10px' }} className={classes.spaceBetweenFlex}>
          <Box
            sx={{
              marginLeft: '10px',
            }}
            className={classes.centeredFlex}
          >
            <Typography variant="h6">{sensorSelectionLabel}</Typography>
            {addedSensors.length > 0 && (
              <IconButton
                onClick={() => {
                  dispatch(setSelectedSensors([]));
                  tagSummaryRef?.current?.clearState();
                }}
                style={{ padding: '0', marginLeft: '10px' }}
              >
                <Tooltip title="Remove all locked sensors">
                  <RemoveCircleIcon color="error" />
                </Tooltip>
              </IconButton>
            )}
          </Box>
          <ActiveMarkerSelection />
        </Box>

        <Box
          sx={{
            position: 'relative',
          }}
        >
          {loading && <LoaderWithBackdrop />}
          {selectionType === CalendarSelectionType.week ? (
            <WeekViewPlot
              combinedSensorStats={combinedSensorStats}
              varName={activeMarker}
              valueType={valueType}
              setHoveredPlot={setHeatmapHoverSelection}
              downloadData={downloadData}
              setDownloadData={setDownloadData}
              sourceLabel={sensorSelectionLabel.replaceAll(' ', '-')}
            />
          ) : (
            <CalendarViewPlot
              combinedSensorStats={combinedSensorStats}
              varName={activeMarker}
              valueType={valueType}
              setHoveredPlot={setHeatmapHoverSelection}
              downloadData={downloadData}
              setDownloadData={setDownloadData}
              sourceLabel={sensorSelectionLabel.replaceAll(' ', '-')}
            />
          )}
        </Box>
      </Box>
      <Box
        sx={{
          background: theme.palette.primary.main,
          padding: '10px 0',
          marginTop: '10px',
          borderRadius: '8px',
        }}
      >
        <Box className={classes.spaceBetweenFlex}>
          <Typography variant="h6" style={{ margin: '10px 20px ', textAlign: 'left' }}>
            {getValuesLabelWithValue()}
          </Typography>
          <ToggleButtonGroup
            color="primary"
            size="small"
            value={dataType}
            exclusive
            onChange={(e, value) => {
              if (value !== null) setDataType(value);
            }}
            sx={{ paddingRight: '8px' }}
            aria-label="data-type"
          >
            <ToggleButton sx={{ textTransform: 'inherit' }} value={DataType.Sensor}>
              {DataType.Sensor}
            </ToggleButton>
            <ToggleButton sx={{ textTransform: 'none' }} value={DataType.Tags}>
              {DataType.Tags}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {sensorFilteredStats.size === 0 && (
          <Alert severity="error" style={{ margin: '1rem 0' }}>
            No sensor data found
          </Alert>
        )}

        <div>
          {dataType === DataType.Tags && (
            <TagViewSummary
              sensorFilteredStats={sensorFilteredStats}
              valuesRange={valuesRange}
              summaryType={summaryType}
              ref={tagSummaryRef}
            />
          )}
          {dataType === DataType.Sensor && (
            <CalendarViewSensorArray
              sensorIds={sensorIdsWithData}
              sensorFilteredStats={sensorFilteredStats}
              valuesRange={valuesRange}
              summaryType={summaryType}
            />
          )}
        </div>
      </Box>
    </>
  );
}

export default CalendarViewDataContainer;
